'use client'
import {
   AiMlAutomation,
   BusinessGrow,
   ContactUsForm,
   FAQs,
   GameChangingIdea,
   TechNServicesHeroSection,
   TemplateSlider,
   WordsOfAppreciation,
} from '@/components'
import { FlutterIcon, IOSIcon } from '@/svgs'
import React from 'react'
import CommonTechGrid from '../../Components/CommonTechGrid'
import FlutterFromStrategy from './components/FlutterFromStrategy'
import FlutterWhyChoose from './components/FlutterWhyChoose'

const slides = [
   {
      icon: '/assets/svg/technologies/flutter/corss.svg',
      topText: 'Cross-Platform App Development',
      header: 'Fast & Scalable Apps',
      details:
         "Build high-performance mobile, web, and desktop applications using Flutter's single codebase and native-like UI.",
   },
   {
      icon: '/assets/svg/technologies/flutter/ui.svg',
      topText: 'UI/UX & Animations',
      header: 'Beautiful & Interactive UI',
      details:
         "Create visually stunning interfaces with Flutter's customizable widgets, smooth animations, and Material/Cupertino design.",
   },
   {
      icon: '/assets/svg/technologies/flutter/state.svg',
      topText: 'State Management Solutions',
      header: 'Efficient State Handling',
      details:
         'Manage app state with Provider, Riverpod, BLoC, or GetX for scalable and maintainable Flutter applications.',
   },
   {
      icon: '/assets/svg/technologies/flutter/backend.svg',
      topText: 'Firebase & API Integrations',
      header: 'Seamless Backend Connectivity',
      details:
         'Integrate Firebase, REST APIs, and GraphQL for real-time database, authentication, cloud storage, and push notifications.',
   },
   {
      icon: '/assets/svg/technologies/flutter/flutter.svg',
      topText: 'Performance & Optimization',
      header: 'Optimized Flutter Performance',
      details:
         'Enhance speed with tree-shaking, deferred components, lazy loading, and efficient memory management.',
   },
   {
      icon: '/assets/svg/technologies/flutter/deploy.svg',
      topText: 'Deployment & App Scaling',
      header: 'Deploy Flutter Seamlessly',
      details:
         'Deploy Flutter apps to iOS, Android, web, and desktop through CI/CD, Play Store, and App Store connections.',
   },
]

const gridContent = [
   {
      title: 'Health & Fitness',
      subtitle: 'Flutter for Healthcare',
      description:
         'Develop secure healthcare apps with Flutter. Create HIPAA-enabled telemedicine platforms, AI-based patient monitoring, and real-time diagnostics for frictionless medical services on any device.',
      url: '/assets/svg/technologies/angular/Health.svg',
   },
   {
      title: 'Fintech',
      subtitle: 'Flutter for Banking & Finance',
      description:
         'Enhance financial applications with Flutter. Develop AI-driven mobile banking apps, fraud detection systems, and secure authentication features for seamless and secure transactions.',
      url: '/assets/svg/technologies/angular/Fintech.svg',
   },
   {
      title: 'Restaurant & Hospitality',
      subtitle: 'Flutter for Restaurants & Hospitality',
      description:
         'Optimize restaurant operations with Flutter-powered apps. Deploy AI-based menu suggestions, intelligent reservation booking, and real-time order tracking for enhanced customer interaction',
      url: '/assets/svg/technologies/angular/Food.svg',
   },
   {
      title: 'E-Commerce',
      subtitle: 'Flutter for Enhanced eCommerce',
      description:
         'Boost eCommerce sales with Flutter-powered mobile apps. Allow AI-enabled product suggestions, friction-free checkout flows, and real-time inventory tracking for enhanced conversions.',
      url: '/assets/svg/technologies/angular/Ecommerce.svg',
   },
   {
      title: 'Supply Chain & Logistics',
      subtitle: 'Flutter for Logistics & Supply Chain',
      description:
         'Streamline logistics with Flutter-based mobile solutions. Facilitate real-time shipment tracking, AI-based route optimization, and automated fleet management for optimized supply chain processes.',
      url: '/assets/svg/technologies/angular/Supply.svg',
   },
   {
      title: 'Social Networking',
      subtitle: 'Flutter for Social Networking',
      description:
         'Develop feature-rich social networking apps with Flutter. Implement real-time chat, AI-driven content recommendations, and interactive user interfaces for higher user engagement.',
      url: '/assets/svg/technologies/angular/Social.svg',
   },
   {
      title: 'Gaming & Sports Analytics',
      subtitle: 'Flutter for Gaming & Sports Analytics',
      description:
         'Augment gaming and sports analytics with Flutter. Create real-time leaderboards, AI-based player analytics, and engaging dashboards for enhanced sports and gaming experiences.',
      url: '/assets/svg/technologies/angular/Gaming.svg',
   },
   {
      title: 'Travel & Tourism',
      subtitle: 'Flutter for Travel & Tourism',
      description:
         'Boost travel booking experiences with Flutter-fueled mobile apps. Integrate AI-based itinerary planning, real-time ticketing, and chatbot-powered support for hassle-free travel.',
      url: '/assets/svg/technologies/angular/travel.svg',
   },
   {
      title: 'Real Estate Solutions',
      subtitle: 'Flutter for Real Estate',
      description:
         'Flutter makes real estate apps easier with AI-based property searches, virtual 3D tours, and real-time market insights, making buying and selling property more convenient.',
      url: '/assets/svg/technologies/angular/Real-Estate.svg',
   },
   {
      title: 'Education & E-Learning',
      subtitle: 'Flutter for Education & E-Learning',
      description:
         'Transform digital learning with Flutter-powered LMS apps. Enable AI-driven adaptive learning, interactive video lessons, and automated student assessments for better engagement.',
      url: '/assets/svg/technologies/angular/Edu.svg',
   },
   {
      title: 'Media & Entertainment',
      subtitle: 'Flutter for Media & Entertainment',
      description:
         'Augment media streaming with Flutter-enabled apps. Add AI-based content suggestions, real-time interaction tools, and frictionless UI for customized entertainment experiences.',
      url: '/assets/svg/technologies/angular/Media.svg',
   },
   {
      title: 'Government & Public Sector',
      subtitle: 'Flutter for Government & Public Sector',
      description:
         'Modernize public services with Flutter-powered mobile apps. Develop secure authentication, AI-driven citizen engagement tools, and real-time government service portals for efficiency and accessibility.',
      url: '/assets/svg/technologies/angular/Gov.svg',
   },
]

const faqData = [
   {
      question:
         'What makes Flutter popular for building apps that run on different platforms?',
      answer:
         'Google developed Flutter as an open-source framework for designing and coding apps that are available on all three platforms from one codebase. Its speed, attractive interface and reliable results on all platforms are why it is a popular choice.',
   },
   {
      question:
         'How does Flutter benefit businesses aiming for mobile and web apps?',
      answer:
         'With Flutter, you can design apps for iOS, Android, web and desktop without changing much code which lowers both costs and the time it takes to launch apps. Fast feedback from hot-reload helps you test and fix issues quicker which speeds up developing and delivering the project.',
   },
   {
      question: 'Which application kinds work best with Flutter?',
      answer:
         'Creating high-performing mobile apps, shopping carts and products, social networks, reservation services, finance, and MVPs is made simple with Flutter.',
   },
   {
      question:
         'What are the key advantages of using Flutter over other frameworks?',
      answer:
         'Building on it gives access to outstanding speed, unique widgets, powerful UI features and smooth animation. Unlike hybrid frameworks, it immediately creates ARM code, making the app both speedy and easy to use.',
   },
   {
      question:
         'Is Flutter suitable for large-scale or enterprise-level applications?',
      answer:
         'Flutter supports scaling and production, has modules, integrates with third-party tools, accesses back-end services and features Provider, Bloc or Riverpod for well-defined state storage.',
   },
   {
      question: 'How secure is Flutter for building apps in 2024?',
      answer:
         'Flutter apps can be secured through platform-level security, input validation, encrypted storage, and secure APIs. With proper coding practices, Flutter apps can meet enterprise-grade security requirements.',
   },
   {
      question:
         'What tools and libraries are commonly used with Flutter development?',
      answer:
         'Popular tools include Dart DevTools, Firebase for backend services, Hive and SQFlite for local storage, GetX or Bloc for state management, and third-party UI packages from pub.dev for faster design implementation.',
   },
   {
      question: 'How much time is needed to create a Flutter application?',
      answer:
         'You might build a basic Flutter app in 3–5 weeks, but if the project requires extra features and custom design, it takes 2–5 months.',
   },
   {
      question:
         'Can Flutter apps be deployed across mobile, web, and desktop platforms?',
      answer:
         'Flutter can be used to create apps for Android, iOS, the web, Windows, macOS and Linux, with the same codebase and very small platform adjustments.',
   },
   {
      question:
         'What should I look for in a Flutter development agency or team?',
      answer:
         'Search for a group of developers who are skilled in Dart and Flutter, have created apps for many platforms, are aware of how to optimize apps, clearly understand different UI/UX standards and can integrate and test different systems effectively.',
   },
]
function FlutterPage() {
   return (
      <>
         <TechNServicesHeroSection
            title={`Flutter Development company`}
            subtitle="Flutter Development Services for Cross-Platform Solutions"
            description="Our Flutter development services create high-performance, cross-platform mobile apps with native-like experiences. We ensure fast development cycles, engaging UI/UX, and seamless functionality, providing scalable solutions that boost performance, user engagement, and business growth efficiently."
            tags={[
               'Custom Flutter Mobile Application Development',
               'Scalable and Cross-Platform Flutter Solutions',
               'Mobile App Solutions',
               'High-Performance and Smooth Flutter UI/UX',
               'Flutter Development',
            ]}
            HeroIcon={FlutterIcon}
         />
         <BusinessGrow />
         <AiMlAutomation
            amdesc="We leverage our expertise in developing high-performance, cross-platform Flutter applications for mobile and web. Our custom Flutter solutions ensure seamless UI/UX, optimized performance, and flexibility to meet evolving business needs."
            amsubtitle={'Expert Flutter Development Solutions'}
            amtitle={'Scalable & Secure Flutter Development Services'}
            slides={slides}
         />

         <CommonTechGrid
            gridTitle={'Flutter-Powered Mobile Solutions:'}
            gridSubTitle={
               'Highly Scalable & High-Performance Apps for Any Industry'
            }
            gridTechbody={
               'Building Fast, Cross-Platform, and Visually Stunning Apps with Flutter'
            }
            gridDesc={
               'At Benzatine, we are experts at Flutter development, designing superior performance mobile apps for iOS, Android, and the web. With our agile, scalable, and cost-efficient method, you can achieve smooth UI/UX, native-like speeds, and quicker time-to-market, empowering businesses to launch robust apps from a single codebase.'
            }
            gridContent={gridContent}
         />

         <FlutterFromStrategy />
         <FlutterWhyChoose />
         <GameChangingIdea />

         <section className="template-slider-section page_top_section section_secondary_bg">
            <div className="container">
               <h4 className="section_heading_title tech_title our-successfull-section">
                  Our Successful Web Application Development Stories
               </h4>
               <p className="features_description m-auto mb-4">
                  Innovative solutions that transformed businesses and delivered
                  real results.
               </p>
            </div>
            <div>
               <TemplateSlider />
            </div>
         </section>

         <section className="section appreciation-slider-section page_top_section section_secondary_bg pt-0">
            <WordsOfAppreciation />
         </section>

         <FAQs faqsConst={faqData} />

         <ContactUsForm />
      </>
   )
}

export default FlutterPage
